import type { JSX, ReactNode } from 'react';
import { cx } from './cx';

/**
 * The standard body of a screen: one scrolling column, width-capped and centred,
 * with the screen padding and the 12px module rhythm already applied.
 *
 * A port of `ScreenContent` in `ui/components/ScreenScaffold.kt`, including its
 * 600px cap (`MaxContentWidth`, here `--max-content-width`). The cap is not a
 * phone concern — it is what stops a `LabelValueRow` on a landscape iPad from
 * putting the label at the far left and the value at the far right with a hand's
 * width of nothing between them, at which point the eye stops reading them as a
 * pair.
 *
 * **This is the scroll container, not the document.** Android gets that for free
 * from `verticalScroll` inside a `Scaffold`; here it has to be chosen, and the
 * choice carries two things a scrolling `<body>` would lose: the fixed bottom
 * tab bar stays out of the content's way, and each tab keeps its own scroll
 * position while it is hidden — the web stand-in for Android's
 * `rememberSaveableStateHolder`, which is why switching to Results and back does
 * not throw away where you were on Home.
 *
 * A plain `<div>` rather than `<main>`: all three tab panes stay mounted, and
 * three `<main>` landmarks in one document is worse for a screen reader than
 * none.
 */
export function ScreenContent({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <div className={cx('screen-content', className)}>{children}</div>;
}

/**
 * The screen's header bar — the `TopAppBar` Home, Results and Settings each put
 * above their `ScreenContent`.
 *
 * Transparent container, exactly as Android sets it with
 * `TopAppBarDefaults.topAppBarColors(containerColor = Color.Transparent)`: the
 * page glow has to run underneath unbroken, or the bar reads as a separate slab
 * pasted over the gradient.
 *
 * **No back arrow, and no slot for one.** Every screen behind this is a peer tab,
 * not a pushed destination — and a WebClip has no Back button at all, which is
 * the same platform fact that keeps Android's `TabNavigation.handlesBack` /
 * `backTarget` out of this port.
 *
 * The title is one line and truncates (Android: `maxLines = 1, overflow =
 * Ellipsis`). It must not wrap: `nav_settings` in Portuguese pushing the bar to
 * two lines would shove the whole scrolling column down on the one screen where
 * vertical space is already tightest.
 */
export function TopAppBar({ title, actions }: { title: string; actions?: ReactNode }): JSX.Element {
  return (
    <header className="top-bar">
      {/*
       * An <h1> because it is the heading of the screen the user is on, and
       * VoiceOver's heading rotor is the fastest way to find out which of three
       * identical-looking tabs is showing. index.css strips the UA font size and
       * margins from headings, so the `.t-*` class is the only thing deciding
       * how it renders — an <h1> and a <div> carrying the same class look the
       * same.
       */}
      <h1 className="top-bar__title t-title-large clamp-1">{title}</h1>
      {/* Rendered only when there is something to put in it, so an empty
       * flex child never eats the gap on a screen with no actions. */}
      {actions ? <div className="top-bar__actions">{actions}</div> : null}
    </header>
  );
}
